// ────────────────────────────────────────────────────────────────────
// TeamChatDock — the customer's single surface: talk to the XZYNEXA team,
// upload files, สั่งงาน, and see delivery status. Reuses the Ask-MODS dock
// SHAPE only; the engine is the server-persisted submissions/events thread
// (never AI, never premium-gated). Replaces DeliveryFeed.
// ────────────────────────────────────────────────────────────────────
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Send, Paperclip, Package, Cog, CheckCircle2, FileText, Hexagon,
         ClipboardList, ClipboardCheck, StickyNote, ShieldCheck, Settings, Clock, Reply, X,
         MessageSquarePlus, Landmark, FileDown, Link as LinkIcon, Sparkles, Loader2 } from "lucide-react";
import { apiClient, downloadConversationPdf, downloadIntakeFile, getActiveTenant, getCachedUser, setActiveTenant, STAFF_ROLES } from "@/lib/api";
import { subscribeLiveEvents } from "@/lib/liveEvents";
import { agentChat } from "@/lib/agentApi";
import type { Conversation, ConversationDelivery, ConversationEvent, PresenceEntry } from "@/lib/types";
import { useDock, TrayToggle, ComposerTray } from "./dock";
import { useWorkspaceFocus } from "./WorkspaceContext";
import { PhotoThumb } from "./PhotoMarkup";
import { RevBadge } from "./RevBadge";

// A file-message's meta, once validated — see fileMessageMeta below. Kept
// separate from the raw ConversationEvent["meta"] (unknown-shaped) so the
// render branch below never has to re-check the shape.
// assetId/assetTag (photo-to-asset chat linking) are the file's CURRENT
// routing, looked up LIVE server-side (routers/submissions.py's
// _augment_file_meta) — not a snapshot from upload time — so a link made
// via the bubble's "ผูกกับ asset" action after the message was posted still
// shows up on the next conversation refetch. null on either field = the
// file isn't routed to an asset (yet).
interface FileMessageMeta { fileId: number; filename: string; fileKind: string; assetId: number | null; assetTag: string | null }

// An in-flight (or failed) file upload rendered as an optimistic sender-side
// bubble — LINE-style, the file appears in the timeline the instant it's
// picked, before the network round-trip even starts. `key` is a monotonic
// counter (up-N), never Math.random — stable across re-renders and safe to
// use as a React key / lookup id. `previewUrl` is an object URL for an image
// file (revoked once the entry is dropped or the component unmounts), null
// for anything else. Removed once the post-upload conversation refetch has
// landed the real "message"/meta.type==="file" event, so the pending bubble
// never disappears before the real one is ready to take its place.
interface PendingUpload {
  key: string;
  name: string;
  isImage: boolean;
  previewUrl: string | null;
  pct: number;
  status: "uploading" | "error";
  file: File;
}

// Defensive parse of a "message" event's meta as a file attachment
// (meta.type === "file") — LINE-style: the upload IS the message, so it
// renders inside the bubble instead of text. Malformed/missing fields (most
// importantly a non-number file_id) return null so the caller falls back to
// the plain body/text bubble rather than throwing or rendering a broken chip.
function fileMessageMeta(meta: ConversationEvent["meta"]): FileMessageMeta | null {
  if (!meta || typeof meta !== "object" || meta["type"] !== "file") return null;
  const fileId = meta["file_id"];
  if (typeof fileId !== "number") return null;
  const filename = typeof meta["filename"] === "string" ? meta["filename"] : "ไฟล์";
  const fileKind = typeof meta["file_kind"] === "string" ? meta["file_kind"] : "other";
  const assetId = typeof meta["asset_id"] === "number" ? meta["asset_id"] : null;
  const assetTag = typeof meta["asset_tag"] === "string" && meta["asset_tag"] ? meta["asset_tag"] : null;
  return { fileId, filename, fileKind, assetId, assetTag };
}

// Reply/quote (cowork roadmap 1.3): the denormalized quote snapshot the
// backend stamps onto a reply's own meta (`record_client_message`'s
// `_reply_meta`) — {reply_to, reply_preview: {actor, kind, body}}. Defensive
// parse, same posture as `fileMessageMeta` above: a malformed/missing shape
// (most importantly a non-number reply_to) degrades to null so the bubble
// falls back to rendering its body plainly instead of throwing.
interface ReplyPreview { id: number; actor: string; kind: "file" | "text"; body: string }

function replyPreviewMeta(meta: ConversationEvent["meta"]): ReplyPreview | null {
  if (!meta || typeof meta !== "object") return null;
  const replyTo = meta["reply_to"];
  const preview = meta["reply_preview"];
  if (typeof replyTo !== "number" || !preview || typeof preview !== "object") return null;
  const p = preview as Record<string, unknown>;
  const actor = typeof p["actor"] === "string" ? p["actor"] : "";
  const kind = p["kind"] === "file" ? "file" : "text";
  const body = typeof p["body"] === "string" ? p["body"] : "";
  return { id: replyTo, actor, kind, body };
}

// Decision stamp (roadmap 3.3 — ISO/IEC 17020 §8.4 records angle): the
// defensively-parsed meta.decision a staff caller's stamp-decision endpoint
// merges onto a plain message event — {by, at}. Same defensive posture as
// fileMessageMeta/replyPreviewMeta above: a malformed/missing shape (empty
// or non-string by/at) degrades to null so the bubble just renders without
// the marker rather than throwing.
interface DecisionStamp { by: string; at: string }

function decisionMeta(meta: ConversationEvent["meta"]): DecisionStamp | null {
  if (!meta || typeof meta !== "object") return null;
  const d = meta["decision"];
  if (!d || typeof d !== "object") return null;
  const rec = d as Record<string, unknown>;
  const by = typeof rec["by"] === "string" ? rec["by"] : "";
  const at = typeof rec["at"] === "string" ? rec["at"] : "";
  if (!by || !at) return null;
  return { by, at };
}

// Scrolls the quoted original into view if it's still in the DOM (silently
// no-ops otherwise — a quote pointing at a message scrolled far out of the
// loaded window is a plain miss, not an error).
function scrollToEvent(id: number): void {
  document.querySelector(`[data-ev-id="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
}

const STAGES = [
  { key: "received", label: "ได้รับแล้ว", icon: Package },
  { key: "processing", label: "กำลังทำ", icon: Cog },
  { key: "done", label: "เสร็จ", icon: CheckCircle2 },
] as const;

// Ball-in-court "ตาใครเดิน" (roadmap 1.2) — whose move it is, derived purely
// from the existing status field (no new state, no backend change):
//   completed/cancelled -> "none" (nothing left to wait on)
//   needs_info          -> "customer" (team explicitly asked for something)
//   anything else       -> "team" (submitted/acknowledged/in_progress/draft/
//                          unknown all sit with the team by default)
// Shared by TODAY (the work queue) so the surfaces reading it can
// never disagree about whose turn it is.
export function ballInCourt(status: string): "customer" | "team" | "none" {
  if (status === "completed" || status === "cancelled") return "none";
  if (status === "needs_info") return "customer";
  return "team";
}

// Default queue ordering — staff triage = answer the longest-waiting งาน
// first. Active rows (ball !== "none") sort ahead of completed/cancelled
// ones; within active rows, "team"'s-move rows come before "customer"'s-move
// rows, each group oldest-activity-first (updated_at falling back to
// created_at, ascending = longest-waiting first). Inactive rows keep whatever
// relative order the API returned them in (comparator returns 0 for that
// pair — Array#sort is stable).
//
// It lives HERE, beside ballInCourt, for the same reason BallChip does: the
// derivation and the order it implies must travel together. It moved out of
// SubmissionsScreen on 2026-09-01 when TODAY became the work queue and
// acquired the second caller (spec: 2026-09-01-today-is-the-queue-design.md).
// Structurally typed, not Submission-typed, so this component file does not
// take on the submissions type surface for one comparator.
export interface BallTriageRow {
  status: string;
  updated_at: string | null;
  created_at: string;
}
export function ballTriageCompare(a: BallTriageRow, b: BallTriageRow): number {
  const ballA = ballInCourt(a.status);
  const ballB = ballInCourt(b.status);
  const activeA = ballA !== "none";
  const activeB = ballB !== "none";
  if (activeA !== activeB) return activeA ? -1 : 1;
  if (!activeA) return 0;
  if (ballA !== ballB) return ballA === "team" ? -1 : 1;
  const tA = Date.parse(a.updated_at ?? a.created_at);
  const tB = Date.parse(b.updated_at ?? b.created_at);
  return tA - tB;
}

// Ball-in-court chip (roadmap 1.2) — the AMBER tint goes to "ตาเรา" (staff's
// move) so it reads as a nudge in triage; "รอลูกค้า" stays quiet (hairline +
// ink-4) since the customer, not staff, owns the next move. Nothing renders
// for ball==="none" (completed/cancelled). Extracted here (2026-07-23 cross-
// tenant inbox, PR 2) so the work queue (TODAY since 2026-09-01) and the inbox’s right
// context pane share the EXACT visual — the derivation (ballInCourt, above)
// and its chip were already meant to travel together, they just hadn't been
// asked to share a second caller yet.
export function BallChip({ status }: { status: string }): JSX.Element | null {
  const ball = ballInCourt(status);
  if (ball === "none") return null;
  return ball === "team" ? (
    <span className="font-mono uppercase inline-flex items-center" style={{
      fontSize: 8.5, letterSpacing: ".08em", fontWeight: 700, color: "var(--warn)",
      border: "1px solid rgba(180,83,9,0.4)", borderRadius: 3, padding: "1px 5px",
      background: "rgba(180,83,9,0.08)" }}>ตาเรา</span>
  ) : (
    <span className="font-mono uppercase inline-flex items-center" style={{
      fontSize: 8.5, letterSpacing: ".08em", color: "var(--ink-4)",
      border: "1px solid var(--hairline)", borderRadius: 3, padding: "1px 5px" }}>รอลูกค้า</span>
  );
}

// Message timestamp, e.g. "20 Jul, 14:32" — parses the ISO string as a Date
// (not a string-slice) so it renders in the viewer's local time, matching how
// fmtRecency/ageDays already parse. String-slicing raw UTC digits was wrong
// even after the backend started emitting +00:00 offsets (7h off for Thai
// users vs. actual local time).
function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

// Just the HH:MM part — "เห็นแล้ว {fmtTime}" (roadmap 2.2 read receipts),
// same time formatting fmtWhen already uses, minus the date.
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

// Calendar-day recency label for "อัปเดตล่าสุด …" — compares local calendar
// dates (not raw 24h deltas) so a message from this morning reads "วันนี้"
// even late at night.
function fmtRecency(iso: string): string {
  const then = new Date(iso);
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(then)) / 86_400_000);
  if (days <= 0) return "วันนี้";
  if (days === 1) return "เมื่อวาน";
  return `${days} วันก่อน`;
}

// Stable local-calendar-day key for an ISO timestamp (viewer's timezone), used
// to detect when the timeline crosses into a new day. Invalid dates → null so
// the caller can skip a separator rather than render "Invalid Date".
function localDayKey(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

// LINE-style day separator label: "วันนี้"/"เมื่อวาน" for the two most recent
// local days, else a localized short date (e.g. "18 ก.ค."). Compares local
// calendar dates (same convention as fmtRecency). Invalid dates → null.
export function daySeparatorLabel(iso: string, now: Date): string | null {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(then)) / 86_400_000);
  if (days === 0) return "วันนี้";
  if (days === 1) return "เมื่อวาน";
  return then.toLocaleDateString("th", { day: "numeric", month: "short" });
}

function DaySeparator({ label }: { label: string }): JSX.Element {
  return (
    <div className="flex items-center justify-center py-0.5" data-testid="day-sep">
      <span className="font-mono uppercase px-2 py-0.5"
            style={{ fontSize: 9, letterSpacing: ".06em", color: "var(--ink-4)",
                     background: "var(--paper)", border: "1px solid var(--hairline)" }}>
        {label}
      </span>
    </div>
  );
}

// A small bordered, clickable file reference — used inside delivery cards
// (and, later, message previews) wherever a file row shows up, and as the
// loading/error fallback for a photo thumbnail (PhotoMarkup.tsx's PhotoThumb).
// Takes a minimal structural shape so it works with SubmissionDetail's
// IntakeFile[] (lazily fetched on card expand) without dragging the full type
// through — kind/annotation are optional and unused here, just accepted so
// callers can pass a photo file straight through. The click goes through the
// auth'd blob-fetch helper (downloadIntakeFile) rather than a plain <a href> —
// presigned download_url is not relied upon (null on local dev).
export function FileChip({ file, showSuperseded }: {
  file: {
    id: number; filename: string; kind?: string; annotation?: string | null;
    revision?: string | null; is_current?: boolean;
  };
  /** Show the muted "ถูกแทนที่" marker when this file is superseded — pass
   *  true only from staff-visible contexts (see RevBadge's docstring). */
  showSuperseded?: boolean;
}): JSX.Element {
  const name = file.filename || "ไฟล์";
  const navigate = useNavigate();
  return (
    <div className="inline-flex items-center gap-1 max-w-full">
      <button type="button" onClick={() => { void downloadIntakeFile(file.id, name); }}
              title={name} className="inline-flex items-center gap-1.5 px-2 py-1 min-w-0 font-mono"
              style={{ fontSize: 10.5, color: "var(--ink-2)", background: "var(--paper)",
                       border: "1px solid var(--hairline)" }}>
        <FileText size={11} style={{ color: "var(--ink-4)", flexShrink: 0 }} />
        <span className="truncate">{name}</span>
        <RevBadge revision={file.revision} isCurrent={file.is_current} showSuperseded={showSuperseded} />
      </button>
      {/* Report Review Room (roadmap 3.1) — only a delivered report PDF is
         reviewable (see server/routers/review.py's gate); other kinds never
         show this. Subtle by design: an icon-only button, not a full CTA. */}
      {file.kind === "report" && (
        <button type="button" onClick={() => navigate(`/review/${file.id}`)}
                title="เปิดรีวิวรายงาน" aria-label="รีวิว"
                className="inline-flex items-center gap-1 px-1.5 py-1 shrink-0 font-mono"
                style={{ fontSize: 10, color: "var(--accent-deep)", background: "var(--paper)",
                         border: "1px solid var(--hairline)" }}>
          <MessageSquarePlus size={11} />
          <span>รีวิว</span>
        </button>
      )}
    </div>
  );
}

// Inline asset-reference chip: "⬡ TAG" — a muted bordered token. DISPLAY-ONLY,
// no live data. This is what an [[asset:TAG]] reference degrades to while
// AssetCard's live summary is loading, or when it can't be fetched (network
// error / unknown tag) — so a bad/typo'd tag never breaks the message, it just
// reads as a plain reference. Also used standalone in SystemEventLine, where
// the due_alert body text already carries the full schedule.
function AssetChip({ tag }: { tag: string }): JSX.Element {
  return (
    <span data-testid="asset-chip" className="inline-flex items-center gap-0.5 px-1 mx-0.5 font-mono align-baseline"
          style={{ fontSize: 10, color: "var(--ink-3)", background: "var(--paper)",
                   border: "1px solid var(--hairline)", borderRadius: 3 }}>
      <Hexagon size={9} style={{ color: "var(--ink-4)", flexShrink: 0 }} />
      <span>{tag}</span>
    </span>
  );
}

// "N references to the same tag = ONE fetch" — react-query dedups on this
// exact key across every AssetCard instance in the timeline, and the 60s
// staleTime means re-opening the dock (or a poll-driven remount) doesn't
// refire it either.
const ASSET_SUMMARY_STALE_MS = 60_000;

const DUE_STATE_LABEL: Record<string, string> = {
  overdue: "เกินกำหนด", due_soon: "ใกล้ครบกำหนด", ok: "ปกติ", unknown: "ไม่ทราบกำหนด",
};

// overdue/due-soon get a warning tint (red/amber — same amber the due_alert
// system line already uses below); ok/unknown stay muted ink so a healthy
// schedule doesn't compete visually with an actual warning.
function dueStateColor(state: string): string {
  if (state === "overdue") return "var(--err)";
  if (state === "due_soon") return "var(--warn)";
  return "var(--ink-3)";
}

// Live [[asset:TAG]] mini-card — the flagship-chat "chat on the data" upgrade
// over the old display-only AssetChip (spec: 2026-07-21-flagship-chat.md §B).
// Fetches GET /api/assets/summary?tag=TAG (tenant-scoped, role-aware —
// worst_cml comes back populated ONLY for a staff caller). Degrades to the
// plain AssetChip while loading AND on any error/404 — a bad tag or a slow
// network must never break the surrounding message.
function AssetCard({ tag }: { tag: string }): JSX.Element {
  const cachedUser = getCachedUser();
  const isStaff = !!cachedUser && (STAFF_ROLES as readonly string[]).includes(cachedUser.role);
  const q = useQuery({
    queryKey: ["asset-summary", tag],
    queryFn: () => apiClient.assetSummary(tag),
    staleTime: ASSET_SUMMARY_STALE_MS,
    retry: false,   // a 404/typo'd tag shouldn't retry — just degrade
  });

  if (q.isPending || q.isError || !q.data) return <AssetChip tag={tag} />;

  const s = q.data;
  const dueLabel = DUE_STATE_LABEL[s.due_state] ?? s.due_state;
  // isStaff gates the UI too (not just trusting worst_cml to be null for a
  // customer) — a null worst_cml must never even be considered for render.
  const worst = isStaff ? s.worst_cml : null;

  return (
    <span data-testid="asset-card" className="inline-flex flex-col gap-0.5 mx-0.5 my-0.5 px-2 py-1 align-top font-mono"
          style={{ fontSize: 10, lineHeight: 1.5, color: "var(--ink-2)", background: "var(--paper)",
                   border: "1px solid var(--hairline)", borderRadius: 4, minWidth: 148, maxWidth: "100%" }}>
      <span className="flex items-center gap-1">
        <Hexagon size={10} style={{ color: "var(--accent)", flexShrink: 0 }} />
        <span style={{ color: "var(--ink-1)", fontWeight: 700 }}>{s.tag}</span>
        <span className="uppercase" style={{ fontSize: 8.5, color: "var(--ink-4)" }}>{s.asset_class}</span>
      </span>
      <span style={{ color: dueStateColor(s.due_state) }}>
        {s.next_due ? `ครบกำหนด ${s.next_due} · ${dueLabel}` : dueLabel}
      </span>
      <span style={{ color: "var(--ink-3)" }}>{s.cml_count} CML</span>
      {worst && (
        <span style={{ color: "var(--ink-3)" }}>
          บางสุด {worst.cml_no} {worst.latest_mm}mm · t_min {worst.t_min_mm} · margin {worst.margin_mm}
        </span>
      )}
    </span>
  );
}

// Splits a message body into text runs and inline [[asset:TAG]] live cards.
// The token regex is strict (asset: prefix, 1–64 non-] chars) so ordinary
// brackets in prose are never consumed.
const ASSET_TOKEN = /\[\[asset:([^\]]{1,64})\]\]/g;
export function renderBody(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  let last = 0;
  let i = 0;
  ASSET_TOKEN.lastIndex = 0;
  for (let m = ASSET_TOKEN.exec(text); m !== null; m = ASSET_TOKEN.exec(text)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<AssetCard key={`a${i++}`} tag={m[1].trim()} />);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// ── Shared "unread" semantics (Phase 3 item 1) ─────────────────────────
// Both the always-mounted TeamChatDockMounter (whose own poll now has to
// survive a minimized/closed window — DockBar unmounts a window's body
// otherwise, see dock/DockBar.tsx) and TeamChatBody's own render need the
// exact same "team replied since the seen-watermark" rule. Factored here so
// the two can never drift apart.
// Roadmap 2.1 — SSE (liveEvents.ts) is now the PRIMARY realtime channel:
// TeamChatDockMounter subscribes once and invalidates ["conversation"] the
// instant a poke arrives, which refetches for every observer sharing that
// queryKey (this body's own query included, whether visible or not). These
// plain polls are now only the DEGRADED FALLBACK for a dropped/reconnecting
// stream (or a browser/proxy that can't hold the connection open) — stretched
// from their pre-SSE values (3s/20s) accordingly. A poke-driven refetch is
// still ~instant; a poll-driven one just heals a miss within this window.
const CONVERSATION_POLL_VISIBLE_MS = 30_000;
const CONVERSATION_POLL_HIDDEN_MS = 60_000;

const CHAT_SEEN_KEY = "xzynexa.chatSeen";

export function getSeenWatermark(): string {
  try { return localStorage.getItem(CHAT_SEEN_KEY) ?? ""; } catch { return ""; }
}
function setSeenWatermark(at: string): void {
  try { localStorage.setItem(CHAT_SEEN_KEY, at); } catch { /* storage unavailable */ }
}

// Newest staff message timestamp in the conversation ("" if none yet).
// Exported (2026-07-24 workbench shell) — TodayScreen's customer "บทสนทนาที่
// ยังไม่ตอบ" section needs the exact same "team replied, customer hasn't
// seen it" rule this and computeUnread/getSeenWatermark already encode, so
// the dock's unread pill and the home tray's row can never disagree.
export function lastTeamMessageAt(conv: Conversation | undefined): string {
  let at = "";
  for (const e of conv?.events ?? []) if (e.kind === "message" && e.actor_role === "staff" && e.created_at > at) at = e.created_at;
  return at;
}

// True when the team has replied since the watermark AND this isn't the
// window currently on screen — the rule the minimized pill's dot has always
// used (see TeamChatBody below, pre-Phase-3).
export function computeUnread(lastTeamAt: string, seen: string, visible: boolean): boolean {
  return !visible && !!lastTeamAt && lastTeamAt > seen;
}

// One-shot "open chat on next load" marker (Phase 3 item 2). AppShell's
// NotificationBell lives in the header, OUTSIDE <DockProvider> (which only
// wraps the page body below it) — it has no useDock() to open the "chat"
// window directly. A "conversation" notification click writes this marker
// (switching the active tenant first, for staff, when needed) and reloads;
// TeamChatDockMounter's own mount effect below reads + clears it exactly
// once, restoring the window instead of leaving it as an unopened pill.
const OPEN_CHAT_MARKER_KEY = "xzynexa.openChatOnLoad";

/** Called by AppShell's NotificationBell on a "conversation" notification
 * click. Staff: switch the active tenant first when it differs from the one
 * already active (same RLS-scope reload TenantSwitcher already uses).
 * Customer: single tenant, no switch — but still reloads, since a reload is
 * the only channel back to the mounter available from outside the provider. */
export function openChatForTenant(isStaff: boolean, tenantId: number | null): void {
  try { localStorage.setItem(OPEN_CHAT_MARKER_KEY, "1"); } catch { /* storage unavailable */ }
  if (isStaff && tenantId != null && String(tenantId) !== getActiveTenant()) {
    setActiveTenant(tenantId);
  }
  window.location.reload();
}

type Row =
  | { t: "delivery"; at: string; d: ConversationDelivery }
  | { t: "event"; at: string; e: ConversationEvent };

// ── Honest presence (roadmap 2.3) ──────────────────────────────────────
// The heartbeat-backed replacement for the hardcoded #15803D dot removed
// months ago (see the "no false presence" test below — a solo team can't
// honor a permanent "we're online" claim, but a live heartbeat can). The
// dot's color is unchanged (the ok token) — what changed is that it only
// renders when GET /api/presence actually returned a live entry for the
// OTHER side, not unconditionally.
const PRESENCE_STALE_MS = 25_000;
const PRESENCE_POLL_MS = 45_000;

// "The other side" relative to this viewer, oldest-heartbeat-first isn't
// the point here — freshest first, so a multi-user side shows whoever's
// most clearly still there if callers ever want entry[0] specifically.
function otherSideEntries(entries: PresenceEntry[], isStaff: boolean): PresenceEntry[] {
  const wantSide = isStaff ? "client" : "staff";
  return entries.filter((e) => e.side === wantSide)
                .sort((a, b) => a.ts_age_seconds - b.ts_age_seconds);
}

// Absence of presence is silence, never "offline" (an offline claim is its
// own honesty trap — see roadmap note) — this renders NOTHING when the
// other side has no live entry, not a muted/greyed "not online" state.
function PresenceStrip({ isStaff }: { isStaff: boolean }): JSX.Element | null {
  const presenceQ = useQuery({
    queryKey: ["presence"],
    queryFn: () => apiClient.presence(),
    staleTime: PRESENCE_STALE_MS,
    refetchInterval: PRESENCE_POLL_MS,
  });
  const others = useMemo(() => otherSideEntries(presenceQ.data ?? [], isStaff), [presenceQ.data, isStaff]);
  if (others.length === 0) return null;

  const text = isStaff
    // Staff viewer: who on the client side is looking, and at what.
    ? `${others[0].name} กำลังดู · ${others[0].label}`
    // Customer viewer: >1 staff collapses to a count (LINE-style "the team",
    // never a specific staffer's page — a customer doesn't need to know
    // WHICH two people are looking, just that the team is there).
    : others.length > 1
      ? `ทีม XZYNEXA ออนไลน์ (${others.length})`
      : `ทีม XZYNEXA ออนไลน์ · ${others[0].label}`;

  return (
    <div data-testid="presence-strip"
         className="flex items-center gap-1.5 px-3 py-1 border-b shrink-0"
         style={{ borderColor: "var(--hairline)", background: "var(--paper)" }}>
      <span aria-hidden style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--ok)", flexShrink: 0 }} />
      <span className="font-mono truncate" style={{ fontSize: 10.5, color: "var(--ink-3)" }}>{text}</span>
    </div>
  );
}

// TeamChatBody — the whole chat surface (header/promise line, timeline,
// composer) merged from the old TeamChatDock (toggle + polling) and its
// inner Panel. Renders WITHOUT its own fixed positioning or toggle button —
// those are now the DockWindow/DockBar's job (see TeamChatDockMounter
// below). `visible` replaces the old local `open` state: it tells this body
// whether its window is actually the one on screen right now (open AND not
// minimized), which still drives the 3s/20s poll split and the "seen"
// watermark exactly as before. `onUnreadChange` reports the computed unread
// flag up to the mounter so it can show the dot on the minimized DockPill.
export function TeamChatBody({ visible = true, onUnreadChange }: {
  visible?: boolean;
  onUnreadChange?: (unread: boolean) => void;
}): JSX.Element {
  // Same STAFF_ROLES source AppShell uses to gate nav/mounts — no second
  // notion of "staff" invented here. Determines the role-aware variant: a
  // customer never sees internal notes or the "รับเป็นงาน" action, and staff
  // never see the customer's "ขอให้ตรวจ" affordance.
  const cachedUser = getCachedUser();
  const isStaff = !!cachedUser && (STAFF_ROLES as readonly string[]).includes(cachedUser.role);
  // See CONVERSATION_POLL_*_MS above — SSE is primary now, this is just the
  // degraded fallback cadence. Drop staleTime so a manual invalidate (e.g.
  // after send, or a live poke) always refetches rather than serving cache.
  const convQ = useQuery({ queryKey: ["conversation"], queryFn: () => apiClient.submissionConversation(),
                           staleTime: 0,
                           refetchInterval: visible ? CONVERSATION_POLL_VISIBLE_MS : CONVERSATION_POLL_HIDDEN_MS });
  const conv = convQ.data;
  const isError = convQ.isError;
  const isPending = convQ.isPending;
  const lastTeamAt = useMemo(() => lastTeamMessageAt(conv), [conv]);
  const [seen, setSeen] = useState<string>(() => getSeenWatermark());
  useEffect(() => {
    if (visible && lastTeamAt && lastTeamAt > seen) {
      setSeenWatermark(lastTeamAt);
      setSeen(lastTeamAt);
    }
  }, [visible, lastTeamAt, seen]);
  const unread = computeUnread(lastTeamAt, seen, visible);
  useEffect(() => { onUnreadChange?.(unread); }, [unread, onUnreadChange]);

  // ── Read receipts (roadmap 2.2) ────────────────────────────────────────
  // Mark-seen: report the newest event id in the thread whenever it advances
  // AND this window is actually on screen (same `visible` gate the unread
  // watermark above uses) — never while minimized/closed. Forward-only on
  // the server (mark_conversation_seen no-ops a stale/duplicate id), so this
  // effect only needs its own "don't repeat the same id" throttle, not a
  // full de-dup.
  const markSeen = useMutation({ mutationFn: (id: number) => apiClient.submissionMarkConversationSeen(id) });
  const reportedSeenIdRef = useRef(0);
  const newestEventId = useMemo(() => {
    let max: number | null = null;
    for (const e of conv?.events ?? []) if (max === null || e.id > max) max = e.id;
    return max;
  }, [conv]);
  useEffect(() => {
    if (!visible || newestEventId === null || newestEventId <= reportedSeenIdRef.current) return;
    reportedSeenIdRef.current = newestEventId;
    markSeen.mutate(newestEventId);
    // markSeen.mutate is intentionally NOT a dependency: this effect's job is
    // "report once per newest id", not "resubscribe whenever useMutation
    // hands back a new (unstable) mutate identity" — that would re-fire on
    // every unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, newestEventId]);

  // The single bubble that gets "เห็นแล้ว HH:MM" instead of a plain
  // timestamp: the NEWEST own-side, non-internal message whose id is <= the
  // OTHER side's last-seen watermark. "Own-side" = actor_role matches this
  // viewer's side — a staff viewer sees the receipt on staff messages, a
  // customer viewer on client messages. Internal notes are excluded by
  // construction (kind !== "message" skips them): a customer can never see
  // a note, so a receipt on one would be a lie.
  const otherSideLastSeenId = isStaff ? conv?.seen.customer_last_event_id ?? null
                                       : conv?.seen.staff_last_event_id ?? null;
  const otherSideSeenAt = isStaff ? conv?.seen.customer_seen_at ?? null
                                   : conv?.seen.staff_seen_at ?? null;
  const viewerSideRole = isStaff ? "staff" : "client";
  const seenMessageId = useMemo(() => {
    if (otherSideLastSeenId === null) return null;
    let best: number | null = null;
    for (const e of conv?.events ?? []) {
      if (e.kind !== "message" || e.actor_role !== viewerSideRole || e.id > otherSideLastSeenId) continue;
      if (best === null || e.id > best) best = e.id;
    }
    return best;
  }, [conv, otherSideLastSeenId, viewerSideRole]);

  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [noteMode, setNoteMode] = useState(false);   // staff-only composer toggle
  const [toolsOpen, setToolsOpen] = useState(false); // the "+" attach-tools tray
  const [assetOpen, setAssetOpen] = useState(false);
  const [assetTag, setAssetTag] = useState("");
  const [postErr, setPostErr] = useState(false);
  // MODS drafted staff replies (human-in-loop): staff-only, per-message
  // action (see EventBubble's onDraft below). `draftingId` gates the button's
  // spinner to the ONE bubble actually in flight (not a global busy flag) —
  // and doubles as a re-entrancy guard, since only one composer exists to
  // receive the result. The current workspace focus (asset-scoped question
  // drafts better) rides along on every call, same as MODS's own ChatDock.
  const { focus } = useWorkspaceFocus();
  const [draftingId, setDraftingId] = useState<number | null>(null);
  const [draftErr, setDraftErr] = useState(false);
  // Dismissible "ร่างจาก MODS · แก้ก่อนส่ง" hint over the composer — the
  // liability-safe marker: MODS never speaks to the customer directly, staff
  // edits and sends this under the team's name, so nothing AI-disclosed
  // reaches the customer side. Cleared on dismiss or on an actual send (see
  // `post`'s onMutate below), not merely on text change (typing edits should
  // NOT silently drop the "unverified draft" warning).
  const [draftHint, setDraftHint] = useState(false);
  const [justUploaded, setJustUploaded] = useState(false);
  const [reqWorkErr, setReqWorkErr] = useState(false);
  const [acceptErr, setAcceptErr] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState(false);
  const [drag, setDrag] = useState(false);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  // Reply/quote composer state (roadmap 1.3): the bubble being answered, or
  // null. Carries its own snapshot (actor/snippet/kind) rather than just an
  // id — the quote bar renders instantly from this without waiting on a
  // refetch, and the same shape rides along in the send mutation's variables
  // (see `post` below) so the optimistic bubble's quote is never a beat
  // behind the real one.
  const [replyTo, setReplyTo] = useState<{ id: number; actor: string; snippet: string; kind: "text" | "file" } | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const textInput = useRef<HTMLInputElement>(null);
  const uploadKeyRef = useRef(0);
  // Mirrors `pending` for the unmount-cleanup effect below, which needs the
  // LATEST previews (an effect with an empty dep array only ever sees the
  // array from its first render otherwise).
  const pendingRef = useRef<PendingUpload[]>([]);
  useEffect(() => { pendingRef.current = pending; }, [pending]);
  useEffect(() => () => {
    for (const p of pendingRef.current) if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
  }, []);

  const rows: Row[] = useMemo(() => {
    if (!conv) return [];
    const out: Row[] = [];
    for (const d of conv.deliveries) out.push({ t: "delivery", at: d.created_at, d });
    for (const e of conv.events) {
      // "note" = a staff-only internal note (kind is how the backend marks
      // it — see record_client_message). Only staff ever receive these from
      // the API, but gate on isStaff too so EventBubble is never asked to
      // decide the customer/staff split on its own. "system" = the
      // integrity system speaking (e.g. a due_alert) — a schedule fact, not
      // a verdict, so both staff and customer see it (spec: flagship-chat A).
      if (e.kind !== "message" && e.kind !== "note" && e.kind !== "system") continue;
      if (e.kind === "note" && !isStaff) continue;
      out.push({ t: "event", at: e.created_at, e });
    }
    return out.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
  }, [conv, isStaff]);

  // Newest event timestamp per submission (falls back to the submission's own
  // created_at when it has no events yet) — drives the "อัปเดตล่าสุด" line.
  // Uses ALL events, not just messages, so a status change alone still counts
  // as an update. Thread events (submission_id NULL) don't belong to any
  // delivery — skip them here.
  const lastActivityBySub = useMemo(() => {
    const map = new Map<number, string>();
    for (const d of conv?.deliveries ?? []) map.set(d.id, d.created_at);
    for (const e of conv?.events ?? []) {
      if (e.submission_id == null) continue;
      const cur = map.get(e.submission_id);
      if (!cur || e.created_at > cur) map.set(e.submission_id, e.created_at);
    }
    return map;
  }, [conv]);

  useEffect(() => { scroller.current?.scrollTo({ top: 1e9 }); }, [rows.length, pending.length]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["conversation"] });
  // True on a brand-new thread (nothing sent yet at all) — drives the welcome
  // panel. Kept as one flag so the welcome panel's own "ขอให้ตรวจ" button and
  // the composer's persistent one are never both on screen at once.
  const firstRun = !isError && !isPending && !!conv && conv.deliveries.length === 0 && conv.events.length === 0;

  // internal AND replyTo are both carried in the mutate variables (not read
  // from state inside mutationFn/onMutate): onMutate resets noteMode/replyTo,
  // which would otherwise re-render and make a state-reading mutationFn see
  // the already-cleared value before it fires (the same is_internal race
  // this file already guards against).
  type PostVars = { body: string; internal: boolean; replyTo: typeof replyTo };
  const post = useMutation({
    mutationFn: (v: PostVars) =>
      apiClient.submissionPostConversationMessage({ body: v.body, is_internal: v.internal, reply_to: v.replyTo?.id }),
    // Optimistic: drop the bubble in immediately so sending feels instant —
    // the POST + refetch round-trip (Thailand -> Railway) otherwise leaves a
    // visible gap before the message shows. onSettled reconciles with the
    // server (the temp negative-id row is replaced by the real one on
    // refetch); onError rolls the optimistic row back.
    onMutate: async (v: PostVars): Promise<{ prev: Conversation | undefined }> => {
      await qc.cancelQueries({ queryKey: ["conversation"] });
      const prev = qc.getQueryData<Conversation>(["conversation"]);
      const optimistic: ConversationEvent = {
        id: -Date.now(), submission_id: null,
        kind: v.internal ? "note" : "message",
        actor_name: "", actor_role: isStaff ? "staff" : "client",
        body: v.body,
        meta: v.replyTo
          ? { reply_to: v.replyTo.id, reply_preview: { actor: v.replyTo.actor, kind: v.replyTo.kind, body: v.replyTo.snippet } }
          : null,
        created_at: new Date().toISOString(),
      };
      if (prev) qc.setQueryData<Conversation>(["conversation"],
        { ...prev, events: [...prev.events, optimistic] });
      setText(""); setPostErr(false); setNoteMode(false); setReplyTo(null); setDraftHint(false);
      requestAnimationFrame(() => scroller.current?.scrollTo({ top: 1e9 }));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["conversation"], ctx.prev);
      setPostErr(true);
    },
    onSettled: () => invalidate(),
  });
  const send = () => { if (text.trim() && !post.isPending) post.mutate({ body: text.trim(), internal: isStaff && noteMode, replyTo }); };
  // Bubble's Reply button -> composer quote bar. Snapshots actor + snippet
  // NOW (not just the id) so the quote bar and the optimistic send below
  // never have to re-derive it from a possibly-stale cache read.
  const startReply = (e: ConversationEvent) => {
    const fm = fileMessageMeta(e.meta ?? null);
    const actor = e.actor_name || (e.actor_role === "staff" ? "ทีม XZYNEXA" : "ลูกค้า");
    setReplyTo({ id: e.id, actor, snippet: fm ? fm.filename : (e.body || ""), kind: fm ? "file" : "text" });
    requestAnimationFrame(() => textInput.current?.focus());
  };

  // "MODS ร่างคำตอบ" (human-in-loop drafted replies) — reuses the same
  // deterministic agent endpoint MODS's own dock calls (no new backend
  // surface). `[]` for history: this is a fresh one-shot draft of a single
  // customer question, not a continuation of MODS's own transcript. The
  // fallback answer (no LLM in prod, question didn't match a deterministic
  // route) still lands in the composer — a starting point staff can rewrite
  // from scratch is still faster than a blank box, and the draft hint makes
  // clear it needs review either way.
  const draftReply = async (customerEvent: ConversationEvent) => {
    if (draftingId !== null) return;
    setDraftingId(customerEvent.id);
    setDraftErr(false);
    try {
      const res = await agentChat(customerEvent.body ?? "", [], focus ?? null, undefined);
      // Non-clobbering: an in-progress draft/edit in the composer is never
      // silently overwritten — the MODS draft is appended instead, since
      // staff may already be mid-reply when they reach for a second opinion.
      // A single space, not "\n": the composer is a single-line <input>
      // (Enter already sends, see onKeyDown below), and browsers silently
      // strip CR/LF from a text input's value on assignment — a literal
      // newline here would have run the two strings together invisibly. An
      // empty composer just takes the draft directly.
      setText((cur) => (cur.trim() ? `${cur.trimEnd()} ${res.answer}` : res.answer));
      setDraftHint(true);
      requestAnimationFrame(() => textInput.current?.focus());
    } catch {
      setDraftErr(true);
    } finally {
      setDraftingId(null);
    }
  };

  // The two honest promote paths (spec: "Inquiry vs Work") — deliberate, not
  // automatic. Both just need the conversation to refetch so the new status
  // card appears; neither takes a body.
  const requestWork = useMutation({
    mutationFn: () => apiClient.submissionRequestWork(),
    onSuccess: () => { setReqWorkErr(false); invalidate(); },
    onError: () => setReqWorkErr(true),
  });
  const acceptAsWork = useMutation({
    mutationFn: () => apiClient.submissionAcceptAsWork(),
    onSuccess: () => { setAcceptErr(false); invalidate(); },
    onError: () => setAcceptErr(true),
  });
  // Decision stamps (roadmap 3.3) — staff curates a plain message into an
  // immutable decision record. No un-stamp path exists (see the endpoint's
  // docstring), so onError just leaves the bubble as-is; a retry is a plain
  // re-click, not a rollback.
  const stampDecision = useMutation({
    mutationFn: (eventId: number) => apiClient.submissionStampDecision(eventId),
    onSuccess: () => invalidate(),
  });

  // Conversation PDF export (roadmap 3.3) — a plain download side effect,
  // not react-query state (nothing here is cached data to invalidate). Both
  // roles get this; the document itself decides what's excluded server-side.
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    setExportErr(false);
    try {
      await downloadConversationPdf();
    } catch {
      setExportErr(true);
    } finally {
      setExporting(false);
    }
  };

  // Runs ONE entry's upload, streaming its own pct via onUploadProgress into
  // that entry (matched by key, never by index — entries can finish/retry
  // out of order). Resolves true/false rather than throwing so the caller
  // (sequential batch or a single retry) never needs its own try/catch.
  const runUpload = async (entry: PendingUpload): Promise<boolean> => {
    try {
      await apiClient.intakeUpload(entry.file, "", (pct) => {
        setPending((cur) => cur.map((p) => (p.key === entry.key ? { ...p, pct } : p)));
      }, undefined, "chat");
      setPending((cur) => cur.map((p) => (p.key === entry.key ? { ...p, pct: 100 } : p)));
      return true;
    } catch {
      setPending((cur) => cur.map((p) => (p.key === entry.key ? { ...p, status: "error" as const } : p)));
      return false;
    }
  };

  // Drops the succeeded entries' pending bubbles ONLY after the conversation
  // refetch lands (invalidateQueries returns the in-flight refetch promise)
  // — so the real file-message bubble is already in `rows` the instant the
  // pending one disappears, never a flash of empty timeline.
  const settleSucceeded = async (keys: string[]) => {
    if (!keys.length) return;
    await qc.invalidateQueries({ queryKey: ["conversation"] });
    setJustUploaded(true);
    setPending((cur) => {
      for (const p of cur) if (keys.includes(p.key) && p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      return cur.filter((p) => !keys.includes(p.key));
    });
    window.setTimeout(() => setJustUploaded(false), 6000);
  };

  const onFiles = (list: FileList | null) => {
    const files = list ? Array.from(list) : [];
    if (!files.length) return;
    const entries: PendingUpload[] = files.map((file) => {
      const isImage = file.type.startsWith("image/");
      return {
        key: `up-${uploadKeyRef.current++}`,
        name: file.name,
        isImage,
        previewUrl: isImage ? URL.createObjectURL(file) : null,
        pct: 0,
        status: "uploading" as const,
        file,
      };
    });
    setPending((cur) => [...cur, ...entries]);
    void (async () => {
      const succeeded: string[] = [];
      for (const entry of entries) if (await runUpload(entry)) succeeded.push(entry.key);
      await settleSucceeded(succeeded);
    })();
  };

  // Re-runs a single failed entry's upload (the "ลองใหม่" text button).
  const retryUpload = (key: string) => {
    const entry = pending.find((p) => p.key === key);
    if (!entry) return;
    setPending((cur) => cur.map((p) => (p.key === key ? { ...p, status: "uploading" as const, pct: 0 } : p)));
    void (async () => {
      const ok = await runUpload({ ...entry, status: "uploading", pct: 0 });
      await settleSucceeded(ok ? [key] : []);
    })();
  };

  // Drops a failed entry outright (the "ลบ" text button) — no retry, no
  // conversation refetch, just the bubble and its preview blob going away.
  const removeUpload = (key: string) => {
    setPending((cur) => {
      const target = cur.find((p) => p.key === key);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return cur.filter((p) => p.key !== key);
    });
  };
  // Append an inline [[asset:TAG]] token to the composer (a display-only ref).
  const attachAsset = () => {
    const tag = assetTag.trim();
    if (!tag) return;
    setText((t) => `${t}${t && !t.endsWith(" ") ? " " : ""}[[asset:${tag}]] `);
    setAssetTag("");
    setAssetOpen(false);
  };

  // No own fixed positioning, width/height or close (x) here: DockWindow
  // supplies those (title, icon, minimize, close) — see TeamChatDockMounter.
  // `relative` is kept so the drag-overlay below can still anchor via
  // `absolute inset-0` to this panel rather than the viewport.
  return (
    <div className="relative flex flex-col h-full w-full"
         style={{ background: "var(--paper)" }}
         onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
         onDragLeave={() => setDrag(false)}
         onDrop={(e) => { e.preventDefault(); setDrag(false); onFiles(e.dataTransfer.files); }}>
      {/* Sub-header banner removed (declutter): the DockWindow title already
          says แชทกับทีม, and the response-time promise lives in the first-run
          welcome panel — a persistent second header earned no space. Honest
          presence (roadmap 2.3) reuses that same freed slot: unlike the old
          removed banner, this one is conditional truth, not decoration — it
          renders nothing when the other side isn't actually there. */}
      {/* Tiny toolbar row, always rendered (unlike PresenceStrip, which can
          render nothing) — the export affordance needs a stable home, not
          one that shifts depending on presence. Both roles get this. */}
      <div className="flex items-center justify-end gap-1.5 px-2 py-1 border-b shrink-0"
           style={{ borderColor: "var(--hairline)", background: "var(--paper)" }}>
        {exportErr && (
          <span className="font-mono" style={{ fontSize: 9.5, color: "var(--err)" }}>ส่งออกไม่สำเร็จ</span>
        )}
        <button type="button" onClick={() => { void handleExport(); }} disabled={exporting}
                title="ส่งออก PDF" aria-label="ส่งออก PDF"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 disabled:opacity-40 hover:text-accent-deep"
                style={{ fontSize: 9.5, color: "var(--ink-3)" }}>
          <FileDown size={13} />
        </button>
      </div>
      <PresenceStrip isStaff={isStaff} />
      <div ref={scroller} className="flex-1 overflow-auto px-3 py-3 flex flex-col gap-2.5" style={{ background: "var(--paper-2)" }}>
        {isError && <div className="font-mono" style={{ fontSize: 11, color: "var(--err)" }}>โหลดแชทไม่ได้ ลองใหม่</div>}
        {isPending && rows.length === 0 && (
          <div className="font-mono text-center py-6" style={{ fontSize: 11.5, color: "var(--ink-3)" }}>กำลังโหลด…</div>
        )}
        {firstRun && (
          <div className="flex flex-col items-center text-center gap-2 py-8 px-3">
            <Package size={22} style={{ color: "var(--accent)" }} />
            <div className="font-mono" style={{ fontSize: 12.5, color: "var(--ink-1)", fontWeight: 600, lineHeight: 1.4 }}>
              ยินดีต้อนรับ — ส่งไฟล์หรือสั่งงานให้ทีม XZYNEXA ได้เลย
            </div>
            <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>ตอบภายใน 1 วันทำการ</div>
            <button type="button" onClick={() => fileInput.current?.click()}
                    className="font-mono px-3 py-1.5 mt-1 rounded-full"
                    style={{ fontSize: 11, color: "var(--ink-1)",
                             background: "var(--accent)", border: "1px solid var(--accent)", fontWeight: 700 }}>
              ส่งไฟล์แรก
            </button>
            {!isStaff && (
              // Quiet, secondary — a deliberate "promote to งาน" action, not a
              // loud CTA competing with "ส่งไฟล์แรก". Only a customer opens a
              // งาน this way; staff use "รับเป็นงาน" instead. Same pill as the
              // composer's persistent copy of this action below — one style
              // for one action, not two.
              <button type="button" onClick={() => requestWork.mutate()} disabled={requestWork.isPending}
                      className="font-mono inline-flex items-center gap-1 px-2.5 py-1 rounded-full disabled:opacity-40 hover:border-accent hover:text-accent-deep"
                      style={{ fontSize: 10, color: "var(--ink-3)",
                               background: "transparent", border: "1px solid var(--hairline)" }}>
                <ClipboardList size={11} /> {requestWork.isPending ? "กำลังส่งคำขอ…" : "ขอให้ตรวจ/ประเมิน"}
              </button>
            )}
            <div className="font-mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>หรือพิมพ์ข้อความด้านล่าง</div>
          </div>
        )}
        {(() => {
          const now = new Date();
          let prevDay: string | null = null;
          return rows.map((r) => {
            const dayKey = localDayKey(r.at);
            // Separator only on a day CHANGE between items — the first group
            // gets none (nothing to separate it from).
            const showSep = dayKey !== null && prevDay !== null && dayKey !== prevDay;
            if (dayKey !== null) prevDay = dayKey;
            const sepLabel = showSep ? daySeparatorLabel(r.at, now) : null;
            const body = r.t === "delivery"
              ? <DeliveryCard key={`d${r.d.id}`} d={r.d} updatedAt={lastActivityBySub.get(r.d.id) ?? r.d.created_at}
                  isStaff={isStaff}
                  autoExpand={justUploaded && r.d.id === (conv?.open_delivery_id ?? -1)} />
              : r.e.kind === "system"
              ? <SystemEventLine key={`e${r.e.id}`} e={r.e} />
              : <EventBubble key={`e${r.e.id}`} e={r.e}
                  onAccept={isStaff && r.e.kind === "message" && r.e.actor_role !== "staff"
                    ? () => acceptAsWork.mutate() : undefined}
                  accepting={acceptAsWork.isPending}
                  onReply={r.e.kind !== "note" ? () => startReply(r.e) : undefined}
                  onStamp={isStaff && r.e.kind === "message" && !decisionMeta(r.e.meta ?? null)
                    ? () => stampDecision.mutate(r.e.id) : undefined}
                  stamping={stampDecision.isPending}
                  // MODS ร่างคำตอบ — staff-only, and only on an actual customer
                  // TEXT question: excludes staff's own messages, internal
                  // notes (r.e.kind === "note" already fails the "message"
                  // check), and file-only messages (fileMessageMeta present,
                  // body null) — there's no question text to draft against.
                  onDraft={isStaff && r.e.kind === "message" && r.e.actor_role !== "staff"
                      && !fileMessageMeta(r.e.meta ?? null) && !!r.e.body
                    ? () => draftReply(r.e) : undefined}
                  drafting={draftingId === r.e.id}
                  seenAt={r.e.id === seenMessageId ? otherSideSeenAt : null} />;
            if (!sepLabel) return body;
            return (
              <Fragment key={`grp-${r.t}${r.t === "delivery" ? r.d.id : r.e.id}`}>
                <DaySeparator label={sepLabel} />
                {body}
              </Fragment>
            );
          });
        })()}
        {pending.map((p) => (
          <PendingBubble key={p.key} p={p} isStaff={isStaff} onRetry={retryUpload} onRemove={removeUpload} />
        ))}
      </div>

      {drag && (
        <div className="absolute inset-0 flex items-center justify-center font-mono pointer-events-none"
             style={{ background: "rgba(var(--accent-rgb) / 0.12)", border: "2px dashed var(--accent)", fontSize: 13, color: "var(--accent-deep)" }}>
          วางไฟล์เพื่อส่งให้ทีม
        </div>
      )}

      <div className="border-t px-2.5 py-2" style={{ borderColor: "var(--hairline)" }}>
        {postErr && <div className="font-mono mb-1" style={{ fontSize: 10, color: "var(--err)" }}>ส่งไม่สำเร็จ ลองใหม่</div>}
        {draftErr && <div className="font-mono mb-1" style={{ fontSize: 10, color: "var(--err)" }}>MODS ร่างคำตอบไม่สำเร็จ ลองใหม่</div>}
        {!isStaff && reqWorkErr && (
          <div className="font-mono mb-1" style={{ fontSize: 10, color: "var(--err)" }}>ส่งคำขอไม่สำเร็จ ลองใหม่</div>
        )}
        {isStaff && acceptErr && (
          <div className="font-mono mb-1" style={{ fontSize: 10, color: "var(--err)" }}>รับเป็นงานไม่สำเร็จ ลองใหม่</div>
        )}
        {!isStaff && !firstRun && (
          // Always-available (not just first-run): a plain question stays a
          // plain question until the customer deliberately promotes it — this
          // is that promotion, kept quiet on purpose (border, not fill). Hidden
          // during firstRun so it doesn't duplicate the welcome panel's own
          // copy of this same button.
          <div className="flex items-center mb-1.5">
            <button type="button" onClick={() => requestWork.mutate()} disabled={requestWork.isPending}
                    className="font-mono inline-flex items-center gap-1 px-2.5 py-1 rounded-full disabled:opacity-40 hover:border-accent hover:text-accent-deep"
                    style={{ fontSize: 10, color: "var(--ink-3)",
                             background: "transparent", border: "1px solid var(--hairline)" }}>
              <ClipboardList size={11} /> {requestWork.isPending ? "กำลังส่งคำขอ…" : "ขอให้ตรวจ/ประเมิน"}
            </button>
          </div>
        )}
        {isStaff && noteMode && (
          <div className="font-mono mb-1" style={{ fontSize: 10, color: "var(--warn)" }}>
            โน้ตภายใน — ลูกค้าจะไม่เห็นข้อความนี้
          </div>
        )}
        {replyTo && (
          // Quote bar (roadmap 1.3): a hairline strip with the accent left
          // border, mirroring the in-bubble quote block's own left-border
          // idiom so the two read as the same affordance.
          <div data-testid="reply-bar" className="flex items-center gap-1.5 mb-1.5 pl-2 pr-1 py-1"
               style={{ background: "var(--paper-2)", border: "1px solid var(--hairline)",
                        borderLeft: "2px solid var(--accent)" }}>
            <div className="flex-1 min-w-0">
              <div className="font-mono truncate" style={{ fontSize: 10, fontWeight: 700, color: "var(--ink-2)" }}>
                ตอบกลับ {replyTo.actor}
              </div>
              <div className="font-mono truncate" style={{ fontSize: 10, color: "var(--ink-4)" }}>{replyTo.snippet}</div>
            </div>
            <button type="button" onClick={() => setReplyTo(null)} aria-label="ยกเลิกการตอบกลับ" className="p-1 shrink-0">
              <X size={13} style={{ color: "var(--ink-4)" }} />
            </button>
          </div>
        )}
        {draftHint && (
          // Liability-safe marker (spec: "MODS never speaks to the customer
          // directly"): staff edits and sends this text under the team's own
          // name via the ordinary send path below, so no AI-disclosure ever
          // reaches the customer — this banner is the staff-side substitute,
          // making the machine origin and "unverified" status explicit right
          // where the text is about to be edited. Dismissible on its own
          // (doesn't block sending); also cleared automatically once the
          // message is actually sent (see `post`'s onMutate above).
          <div data-testid="draft-hint" className="flex items-center gap-1.5 mb-1.5 px-2 py-1"
               style={{ background: "rgba(var(--accent-rgb) / 0.08)", border: "1px solid var(--accent)", borderRadius: 6 }}>
            <Sparkles size={11} style={{ color: "var(--accent)", flexShrink: 0 }} />
            <span className="font-mono flex-1" style={{ fontSize: 10, color: "var(--ink-2)" }}>
              ร่างจาก MODS · แก้ก่อนส่ง
            </span>
            <button type="button" onClick={() => setDraftHint(false)} aria-label="ปิดคำแนะนำ" className="p-0.5 shrink-0">
              <X size={12} style={{ color: "var(--ink-4)" }} />
            </button>
          </div>
        )}
        {assetOpen && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="font-mono" style={{ fontSize: 10, color: "var(--ink-4)", whiteSpace: "nowrap" }}>อ้างอิง asset</span>
            <input autoFocus value={assetTag} onChange={(e) => setAssetTag(e.target.value)} aria-label="แท็ก asset"
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); attachAsset(); } if (e.key === "Escape") setAssetOpen(false); }}
                   placeholder="เช่น V-301" className="flex-1 min-w-0 px-2 py-1 outline-none font-mono"
                   style={{ fontSize: 11, background: "var(--paper-2)", border: "1px solid var(--hairline)", borderRadius: 12, color: "var(--ink-1)" }} />
            <button type="button" onClick={attachAsset} className="font-mono px-2.5 py-1 rounded-full shrink-0"
                    style={{ fontSize: 10, color: "var(--accent-deep)", border: "1px solid var(--accent)" }}>เพิ่ม</button>
          </div>
        )}
        <div className="flex items-center gap-0.5">
          <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
          {/* LINE-style "+" attach tray — the SHARED TrayToggle/ComposerTray
              (dock/ComposerTray.tsx), so the press-+ effect is identical to
              MODS's by construction. Picking a tool acts immediately and
              collapses the tray — active states stay visible elsewhere
              (noteMode's amber line, assetOpen's tag row). */}
          <TrayToggle open={toolsOpen} onClick={() => setToolsOpen((v) => !v)} />
          <ComposerTray open={toolsOpen} width={110}>
            <button onClick={() => { fileInput.current?.click(); setToolsOpen(false); }} aria-label="แนบไฟล์"
                    tabIndex={toolsOpen ? 0 : -1} className="p-1 shrink-0">
              <Paperclip size={16} style={{ color: "var(--ink-3)" }} />
            </button>
            <button type="button" onClick={() => { setAssetOpen((v) => !v); setToolsOpen(false); }} aria-label="แนบ asset"
                    tabIndex={toolsOpen ? 0 : -1} className="p-1 shrink-0">
              <Hexagon size={15} style={{ color: assetOpen ? "var(--accent-deep)" : "var(--ink-3)" }} />
            </button>
            {isStaff && (
              <button type="button" onClick={() => { setNoteMode((v) => !v); setToolsOpen(false); }} aria-label="โน้ตภายใน"
                      aria-pressed={noteMode} title="โพสต์เป็นโน้ตภายใน (ทีมเท่านั้น)"
                      tabIndex={toolsOpen ? 0 : -1} className="p-1 shrink-0">
                <StickyNote size={15} style={{ color: noteMode ? "var(--warn)" : "var(--ink-3)" }} />
              </button>
            )}
          </ComposerTray>
          <input ref={textInput} value={text} onChange={(e) => { setText(e.target.value); if (postErr) setPostErr(false); }}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                 placeholder={isStaff && noteMode ? "พิมพ์โน้ตภายใน…" : "พิมพ์ข้อความ…"}
                 className="flex-1 min-w-0 px-3 py-1.5 ml-1 outline-none font-mono"
                 style={{ fontSize: 12, background: "var(--paper-2)", border: "1px solid var(--hairline)", borderRadius: 18, color: "var(--ink-1)" }} />
          <button onClick={send} disabled={post.isPending || !text.trim()} aria-label="ส่ง"
                  className="flex items-center justify-center shrink-0 disabled:opacity-40 transition-colors"
                  style={{ width: 30, height: 30, borderRadius: 999, background: isStaff && noteMode ? "var(--warn)" : "var(--accent)", color: "#fff" }}><Send size={15} /></button>
        </div>
      </div>
    </div>
  );
}

// TeamChatDockMounter — registers the customer/team chat window with the
// shared dock. Mounted by AppShell for staff (only once an active tenant is
// set — the conversation query is scoped to it) and unconditionally for
// customers, where it defaults OPEN (spec: "customer default = ONE window,
// the team chat"). Staff keep the old closed-by-default behavior (a click
// was always required before) — nothing in the spec calls for changing that.
//
// `visible` is derived from the dock's own open/minimized state (replacing
// the old local `open` toggle) and fed into TeamChatBody so its poll
// interval and "seen" watermark still track whether the user is actually
// looking at it. `body` is memoized on `visible` alone so re-registration —
// and the DockContext state update it causes — only happens when something
// that actually matters (visible or unread) changes, not on every unrelated
// dock render (which would otherwise re-run this effect and loop).
//
// Phase 3 item 1 — unread while collapsed: DockBar only renders a window's
// `body` while it's open+!minimized (see dock/DockBar.tsx), so TeamChatBody
// — and the poll that lives inside it — unmounts the moment the window is
// minimized or closed. Before this, the pill's unread dot could never
// update while collapsed (the old outer-dock 20-30s poll that used to
// survive a close/minimize was lost in the Phase 1 migration into windows).
// The fix: this mounter — which, unlike the body, is ALWAYS mounted — runs
// its own ["conversation"] poll (react-query dedups the network calls with
// TeamChatBody's own poll by queryKey while both are alive) and computes
// `unread` itself via the same computeUnread/lastTeamMessageAt helpers
// TeamChatBody uses, so the two can't drift. That poll now ticks at the
// degraded-fallback CONVERSATION_POLL_*_MS cadence (see their definition
// above) — roadmap 2.1 made this mounter the SSE subscriber too (below), so
// the poll's job shrank from "the way unread updates" to "heal a missed poke
// or a dropped stream".
export function TeamChatDockMounter(): JSX.Element | null {
  const { registerWindow, unregisterWindow, openWindow, restore, isOpen, isMinimized } = useDock();
  const cachedUser = getCachedUser();
  const isStaff = !!cachedUser && (STAFF_ROLES as readonly string[]).includes(cachedUser.role);
  const visible = isOpen("chat") && !isMinimized("chat");
  const qc = useQueryClient();

  const convQ = useQuery({
    queryKey: ["conversation"], queryFn: () => apiClient.submissionConversation(),
    staleTime: 0,
    refetchInterval: visible ? CONVERSATION_POLL_VISIBLE_MS : CONVERSATION_POLL_HIDDEN_MS,
  });
  const lastTeamAt = useMemo(() => lastTeamMessageAt(convQ.data), [convQ.data]);
  // Read the watermark fresh rather than caching it in state: it's only ever
  // advanced (by TeamChatBody) while visible, i.e. exactly when this value
  // can't change the result anyway (unread is forced false while visible),
  // so there's no staleness window to worry about — a plain read is simplest.
  const unread = useMemo(
    () => computeUnread(lastTeamAt, getSeenWatermark(), visible),
    [lastTeamAt, visible],
  );

  const body = useMemo(
    () => <TeamChatBody visible={visible} />,
    [visible],
  );

  useEffect(() => {
    registerWindow("chat", {
      title: "แชทกับทีม",
      icon: <MessageSquare size={14} className="text-brand" />,
      body,
      defaultOpen: !isStaff,
      unread,
    });
  }, [registerWindow, body, unread, isStaff]);

  useEffect(() => () => unregisterWindow("chat"), [unregisterWindow]);

  // Roadmap 2.1 — SSE subscription. ONE subscription for the whole mounter
  // (not per-window-visibility) since a poke must be caught even while the
  // window is minimized/closed — that's exactly what drives the unread dot
  // to update near-instantly instead of waiting on the (now-degraded)
  // fallback poll. Mount-once/unmount-once: subscribeLiveEvents itself is
  // silent-no-op pre-login and handles its own reconnect/backoff, so this
  // effect only needs to wire the callback and clean up on unmount.
  useEffect(() => {
    const unsubscribe = subscribeLiveEvents((kind) => {
      if (kind === "conversation") qc.invalidateQueries({ queryKey: ["conversation"] });
      // Roadmap 2.3 — a presence poke means someone's page/label (or
      // presence itself) changed; refetch so the strip inside TeamChatBody
      // (which shares this ["presence"] queryKey) updates near-instantly
      // instead of waiting on its own staleTime/refetchInterval fallback.
      if (kind === "presence") qc.invalidateQueries({ queryKey: ["presence"] });
    });
    return unsubscribe;
  }, [qc]);

  // Phase 3 item 2 — consume the one-shot "open chat on next load" marker
  // exactly once on mount (see openChatForTenant above). restore() opens +
  // un-minimizes + brings the window to front in one call, so a notification
  // click lands the user ON the open chat rather than a pill they still have
  // to click.
  useEffect(() => {
    let marker: string | null = null;
    try { marker = localStorage.getItem(OPEN_CHAT_MARKER_KEY); } catch { /* storage unavailable */ }
    if (!marker) return;
    try { localStorage.removeItem(OPEN_CHAT_MARKER_KEY); } catch { /* storage unavailable */ }
    openWindow("chat");
    restore("chat");
    // Mount-only: this marker is meant to be consumed exactly once per page
    // load, not re-applied on every unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

// Ball-in-court line under the stage pills — the AMBER treatment goes to
// whichever party the ball is actually in front of (credibility, not
// decoration): a customer never gets nagged about their own งาน sitting with
// the team, and staff never get a false sense that a customer-blocked งาน
// needs their attention. ball==="none" (completed/cancelled) renders nothing.
function BallInCourtLine({ status, updatedAt, isStaff }: {
  status: string; updatedAt: string; isStaff: boolean;
}): JSX.Element | null {
  const ball = ballInCourt(status);
  if (ball === "none") return null;
  if (isStaff) {
    return ball === "customer" ? (
      <div className="font-mono mt-1" style={{ fontSize: 10, color: "var(--ink-4)" }}>
        รอลูกค้า (ขอข้อมูลไว้)
      </div>
    ) : (
      <div className="font-mono mt-1" style={{ fontSize: 10, color: "var(--warn)", fontWeight: 700 }}>
        ตาเรา — ลูกค้ากำลังรอ
      </div>
    );
  }
  return ball === "customer" ? (
    <div className="font-mono mt-1 flex items-center gap-1" style={{ fontSize: 10, color: "var(--warn)", fontWeight: 700 }}>
      <Clock size={11} style={{ flexShrink: 0 }} /> รอข้อมูลจากคุณ, ทีมขอเมื่อ {fmtRecency(updatedAt)}
    </div>
  ) : (
    <div className="font-mono mt-1" style={{ fontSize: 10, color: "var(--ink-4)" }}>
      อยู่กับทีม — ไม่ต้องทำอะไรตอนนี้
    </div>
  );
}

function DeliveryCard({ d, updatedAt, isStaff, autoExpand }: {
  d: ConversationDelivery; updatedAt: string; isStaff: boolean;
  /** Pops the card open from outside — used right after a file upload so the
   *  attached file is immediately visible (attaching used to be silent). */
  autoExpand?: boolean;
}): JSX.Element {
  if (d.client_stage === "cancelled") {
    return (
      <div data-delivery-id={d.id} style={{ border: "1px solid var(--hairline)", background: "var(--paper-2)", padding: "8px 10px" }}>
        <div className="flex items-center gap-1.5">
          <Package size={13} style={{ color: "var(--ink-4)" }} />
          <span className="font-mono" style={{ fontSize: 11.5, color: "var(--ink-3)", fontWeight: 600 }}>{d.title}</span>
          <span className="font-mono uppercase ml-auto" style={{ fontSize: 8.5, letterSpacing: ".08em", color: "var(--err)" }}>ยกเลิกแล้ว</span>
        </div>
      </div>
    );
  }
  const idx = STAGES.findIndex((x) => x.key === d.client_stage);
  // Flagship-chat §C: a completed delivery the backend stamped with a
  // sign-off-authorized engineer's name is a formal certified artifact, not
  // a plain "done" card — see CertifiedSeal below. A completed-but-uncertified
  // delivery (staffer without can_sign_off, or predates this feature) keeps
  // the ordinary treatment; client_stage guards against a certified stamp
  // somehow surviving on a non-"done" card (shouldn't happen server-side, but
  // the UI must not trust a stray field over the actual lifecycle stage).
  const certified = d.client_stage === "done" && !!d.certified_by;
  // A positive file_count and/or an outcome make the card expandable; the header
  // stays a plain block otherwise (no dead disclosure affordance). Collapsed by
  // default so the timeline reads as a compact status list; details are on
  // demand. file_count comes from the conversation payload (cheap); the file
  // LIST is fetched lazily only when the card is expanded — the conversation
  // poll no longer embeds it.
  const expandable = d.file_count > 0 || !!d.outcome;
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { if (autoExpand) setExpanded(true); }, [autoExpand]);
  // Lazy per-card file fetch: enabled only while expanded, so a collapsed card
  // (the common case in a polled timeline) never hits the network. Reuses
  // GET /api/submissions/{id} → SubmissionDetail.files (IntakeFile[]).
  const filesQ = useQuery({
    queryKey: ["submission-files", d.id],
    queryFn: () => apiClient.submission(d.id),
    enabled: expanded && d.file_count > 0,   // outcome-only cards never fetch
    staleTime: 60_000,
  });

  const header = (
    <>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Package size={13} style={{ color: "var(--accent)" }} />
        <span className="font-mono" style={{ fontSize: 11.5, color: "var(--ink-1)", fontWeight: 600 }}>{d.title}</span>
      </div>
      <div className="flex items-center gap-1">
        {STAGES.map((st, i) => {
          const on = i <= idx;
          const Icon = st.icon;
          return (
            <span key={st.key} className="flex items-center gap-1">
              {i > 0 && <span style={{ width: 14, height: 1, background: on ? "var(--accent)" : "var(--hairline)" }} />}
              <span className="font-mono inline-flex items-center gap-0.5 uppercase"
                    style={{ fontSize: 8.5, fontWeight: on ? 700 : 400,
                             color: on ? (st.key === "done" ? "var(--ok)" : "var(--accent-deep)") : "var(--ink-3)" }}>
                {/* 3-channel active/inactive signal (never color-only): weight
                    above (700 vs 400) + fill here (filled vs outline icon). */}
                <Icon size={10} fill={on ? "currentColor" : "none"} /> {st.label}
              </span>
            </span>
          );
        })}
      </div>
      {certified && <CertifiedSeal d={d} />}
      {!!d.client_approved_by && <ApprovedLine d={d} />}
      <BallInCourtLine status={d.status} updatedAt={updatedAt} isStaff={isStaff} />
      <div className="font-mono mt-1 flex items-center gap-1" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
        <span>อัปเดตล่าสุด {fmtRecency(updatedAt)}</span>
        {expandable && (
          <span className="uppercase" style={{ letterSpacing: ".06em" }}>
            {d.file_count > 0 ? `· ${d.file_count} ไฟล์ ` : "· "}{expanded ? "▲" : "▼"}
          </span>
        )}
      </div>
    </>
  );

  // Certified cards get the success-tint hairline treatment instead of the
  // ordinary accent-orange one — credible/engineering (a subtle border +
  // tint), not decorative, and visually distinct at a glance from a plain
  // in-progress/completed card.
  const border = certified ? "var(--ok)" : "var(--accent)";
  const bg = certified ? "rgba(21,128,61,0.06)" : "rgba(var(--accent-rgb) / 0.08)";

  return (
    <div data-delivery-id={d.id} style={{ border: `1px solid ${border}`, background: bg, padding: "8px 10px" }}>
      {expandable ? (
        <button type="button" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}
                className="block w-full text-left">
          {header}
        </button>
      ) : header}
      {expandable && expanded && (
        <div className="mt-2 flex flex-col gap-1.5">
          {d.file_count > 0 && (
            filesQ.isPending ? (
              <div className="font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>กำลังโหลดไฟล์…</div>
            ) : filesQ.isError ? (
              <div className="font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>โหลดไฟล์ไม่ได้</div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {filesQ.data.files.map((f) => (
                  f.kind === "photo"
                    ? <PhotoThumb key={f.id} file={f} deliveryId={d.id} showSuperseded={isStaff} />
                    : <FileChip key={f.id} file={f} showSuperseded={isStaff} />
                ))}
              </div>
            )
          )}
          {d.outcome && (
            <div className="font-mono" style={{ fontSize: 10.5, color: "var(--ok)" }}>✓ {d.outcome}</div>
          )}
        </div>
      )}
    </div>
  );
}

// Flagship-chat §C — the certified-artifact treatment. A completed delivery
// the backend stamped with a sign-off-authorized engineer's name (see
// transition_submission / _stamp_certification in routers/submissions.py) is
// a formal deliverable, not a plain "done" card: the engineer's name and cert
// number on a delivered report are the accountability claim (same "engineer
// name appears on deliverables" rule the sign-off/PDF arcs already lock in),
// so this renders identically for customer and staff — nothing here is
// staff-only. Sits inside the card header, above the fold, so the seal is
// visible whether or not the card is expanded (the attached report file
// itself still lives behind the existing expand/file-list affordance).
function CertifiedSeal({ d }: { d: ConversationDelivery }): JSX.Element {
  return (
    <div data-testid="certified-seal" className="flex items-start gap-1.5 mt-1.5 pt-1.5"
         style={{ borderTop: "1px solid rgba(21,128,61,0.25)" }}>
      <ShieldCheck size={13} style={{ color: "var(--ok)", flexShrink: 0, marginTop: 1 }} />
      <div className="font-mono" style={{ fontSize: 10, lineHeight: 1.5 }}>
        <div style={{ color: "var(--ok)", fontWeight: 700 }}>รายงานรับรองแล้ว</div>
        <div style={{ color: "var(--ink-2)" }}>
          รับรองโดย {d.certified_by}{d.certified_cert ? ` · ${d.certified_cert}` : ""}
        </div>
        {d.certified_at && (
          <div style={{ color: "var(--ink-4)" }}>{fmtWhen(d.certified_at)}</div>
        )}
      </div>
    </div>
  );
}

// Approve gate (roadmap 3.2) — a quiet single-line confirmation once the
// customer has approved the report (all review pins resolved), matching
// CertifiedSeal's green (--ok) visual family but deliberately smaller/
// quieter: this is a review-time honesty record, not the delivery's formal
// certified-artifact seal (the two are independent — a delivery can be
// approved before it's completed/certified, or completed without ever
// being approved, since approval is a soft gate). Renders for both
// customer and staff — nothing here is side-gated, same rule as
// CertifiedSeal.
function ApprovedLine({ d }: { d: ConversationDelivery }): JSX.Element {
  return (
    <div data-testid="approved-line" className="font-mono flex items-center gap-1 mt-1"
         style={{ fontSize: 9.5, color: "var(--ok)" }}>
      <CheckCircle2 size={11} style={{ flexShrink: 0 }} />
      <span>
        ✓ ลูกค้าอนุมัติรายงานแล้ว — {d.client_approved_by}
        {d.client_approved_at ? ` · ${fmtWhen(d.client_approved_at)}` : ""}
      </span>
    </div>
  );
}

// The integrity system speaking (flagship-chat spec item A) — a centered
// muted line, deliberately NOT a left/right bubble so it never reads as
// either side of the conversation. A due_alert (meta.type === "due_alert")
// gets a warning (amber) tint and the asset tag as a chip; any other/
// malformed system event just degrades to a plain centered line — meta is
// read defensively so a bad payload can never throw the whole timeline.
function SystemEventLine({ e }: { e: ConversationEvent }): JSX.Element {
  let assetTag: string | null = null;
  let isDueAlert = false;
  const meta = e.meta;
  if (meta && typeof meta === "object" && meta["type"] === "due_alert") {
    isDueAlert = true;
    const tag = meta["asset_tag"];
    if (typeof tag === "string" && tag) assetTag = tag;
  }
  return (
    <div className="flex justify-center" data-testid="system-event">
      <div className="font-mono inline-flex items-center gap-1.5 px-2.5 py-1"
           style={{ fontSize: 10.5, lineHeight: 1.4, textAlign: "center", maxWidth: "88%",
                    color: isDueAlert ? "#92400E" : "var(--ink-3)",
                    background: isDueAlert ? "rgba(217,119,6,0.12)" : "var(--paper)",
                    border: `1px solid ${isDueAlert ? "var(--warn)" : "var(--hairline)"}`,
                    borderRadius: 999 }}>
        {assetTag && <AssetChip tag={assetTag} />}
        <span>{e.body ?? ""}</span>
      </div>
    </div>
  );
}

// The optimistic upload bubble — reuses EventBubble's own chrome for the
// SAME side the real message will land on, so a pending upload and the real
// message it becomes never visually jump. A customer upload is sender-side
// (self-end, orange tint, 10px radii with the 2px corner); a staff upload
// (channel="chat" — see intake.upload) posts as a team message like any
// other staff event, so it renders self-start with EventBubble's staff
// chrome (paper background, hairline border, 2px corner mirrored to the
// bottom-left) instead. The timestamp slot is repurposed for live status
// instead of a fixed time: a progress percent while uploading, or an error +
// two tiny text actions (retry/remove) on failure.
function PendingBubble({ p, isStaff, onRetry, onRemove }: {
  p: PendingUpload;
  isStaff: boolean;
  onRetry: (key: string) => void;
  onRemove: (key: string) => void;
}): JSX.Element {
  return (
    <div className={isStaff ? "self-start" : "self-end"} style={{ maxWidth: "82%" }}>
      <div className="font-mono px-2.5 py-1.5" style={{
        fontSize: 12, lineHeight: 1.45,
        background: isStaff ? "var(--paper)" : "rgba(var(--accent-rgb) / 0.1)",
        border: isStaff ? "1px solid var(--hairline)" : "none",
        color: "var(--ink-1)",
        borderRadius: isStaff ? "10px 10px 10px 2px" : "10px 10px 2px 10px" }}>
        {p.isImage && p.previewUrl ? (
          <img src={p.previewUrl} alt={p.name} className="object-cover block"
               style={{ maxWidth: 180, maxHeight: 180, borderRadius: 8,
                        opacity: p.status === "uploading" ? 0.6 : 1 }} />
        ) : (
          <span className="inline-flex items-center gap-1.5 max-w-full">
            <FileText size={13} style={{ color: "var(--ink-4)", flexShrink: 0 }} />
            <span className="truncate">{p.name}</span>
          </span>
        )}
        <div style={{ height: 3, borderRadius: 999, background: "var(--hairline)", marginTop: 6, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${p.pct}%`, background: "var(--accent)",
                        borderRadius: 999, transition: "width 120ms" }} />
        </div>
      </div>
      <div className={`font-mono ${isStaff ? "" : "text-right"}`} style={{ fontSize: 8.5, marginTop: 2 }}>
        {p.status === "error" ? (
          <span className="inline-flex items-center gap-1.5">
            <span style={{ color: "var(--err)" }}>ส่งไม่สำเร็จ</span>
            <button type="button" onClick={() => onRetry(p.key)}
                    className="underline" style={{ color: "var(--ink-3)" }}>ลองใหม่</button>
            <button type="button" onClick={() => onRemove(p.key)}
                    className="underline" style={{ color: "var(--ink-3)" }}>ลบ</button>
          </span>
        ) : (
          <span style={{ color: "var(--ink-3)" }}>กำลังส่ง… {p.pct}%</span>
        )}
      </div>
    </div>
  );
}

function EventBubble({ e, onAccept, accepting, onReply, onStamp, stamping, onDraft, drafting, seenAt }: {
  e: ConversationEvent;
  /** Staff-only, customer-message-only: the "รับเป็นงาน" promote action,
   *  anchored to the message it responds to (a gear on the bubble) instead
   *  of a global pill in the composer. Two-step (gear -> confirm chip) so a
   *  stray click never opens a งาน. */
  onAccept?: () => void;
  accepting?: boolean;
  /** "ตอบกลับ" (roadmap 1.3) — both roles reply to both roles, on any
   *  non-note bubble. Unlike onAccept this is a single click (non-
   *  destructive: it only opens the composer's quote bar), so it carries no
   *  two-step confirm of its own. */
  onReply?: () => void;
  /** Staff-only "ตราเป็นบันทึกการตัดสินใจ" (roadmap 3.3, decision stamps) —
   *  present only on a plain, non-internal message that isn't already
   *  stamped (TeamChatBody withholds this prop once meta.decision exists,
   *  or for anyone but staff). Two-step (icon -> confirm chip), same idiom
   *  as onAccept's gear, because stamping is immutable — there's no
   *  un-stamp to undo a stray click. */
  onStamp?: () => void;
  stamping?: boolean;
  /** MODS drafted staff replies (human-in-loop) — staff-only, customer-TEXT-
   *  message-only (TeamChatBody withholds this for staff's own messages,
   *  internal notes, and file-only messages with no question text). Single
   *  click, no two-step confirm (same posture as onReply: it only fills the
   *  composer, never sends anything on its own — MODS never speaks to the
   *  customer directly, staff always reviews and sends under the team's
   *  name). */
  onDraft?: () => void;
  /** True only while THIS bubble's draft request is in flight (not a global
   *  busy flag) — only one bubble ever shows the spinner at a time. */
  drafting?: boolean;
  /** Roadmap 2.2 read receipts — the other side's seen-at timestamp, set ONLY
   *  on the single newest own-side message they've seen (see `seenMessageId`
   *  in TeamChatBody). Renders "เห็นแล้ว HH:MM" in place of the plain
   *  timestamp on that one bubble; every other bubble gets null and keeps
   *  its ordinary timestamp. */
  seenAt?: string | null;
}): JSX.Element {
  const staff = e.actor_role === "staff";
  // Internal notes (kind="note") only ever reach a staff caller — the Panel
  // row-filter already keeps them out of the customer variant — but the
  // bubble itself must never read as an ordinary team reply, so it gets its
  // own muted/amber treatment and label instead of "ทีม XZYNEXA".
  const isNote = e.kind === "note";
  const [actionsOpen, setActionsOpen] = useState(false);
  const [stampConfirmOpen, setStampConfirmOpen] = useState(false);
  // LINE-style file/photo message: the upload IS the message, rendered
  // inside the same bubble chrome (side/tint/role label/timestamp) as a text
  // message — see fileMessageMeta above for the defensive parse.
  const fileMeta = fileMessageMeta(e.meta ?? null);
  const replyMeta = replyPreviewMeta(e.meta ?? null);
  // Decision stamp (roadmap 3.3) — rendered for BOTH viewers once present,
  // regardless of whether this viewer is staff (staff sees it too, on the
  // bubble they just stamped or one a teammate stamped earlier).
  const decision = decisionMeta(e.meta ?? null);

  // Photo-to-asset chat linking — staff-only, file-bubbles-only. Same
  // getCachedUser/STAFF_ROLES source as AssetCard/TeamChatBody above (no
  // second notion of "staff" invented here); a self-contained mutation +
  // asset-tag lookup, same idiom as ChatDock's ⬡ asset-focus picker
  // (openAssetPick/pickAsset) — a single click opens a small inline
  // datalist row (not the two-step confirm idiom onAccept/onStamp use,
  // since re-routing a file is reversible, unlike accepting or stamping).
  const cachedUser = getCachedUser();
  const isStaff = !!cachedUser && (STAFF_ROLES as readonly string[]).includes(cachedUser.role);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkQuery, setLinkQuery] = useState("");
  const [linkErr, setLinkErr] = useState(false);
  const [linkedOk, setLinkedOk] = useState(false);
  // "N bubbles open the picker = ONE fetch" — react-query dedups on this
  // exact key across every EventBubble instance, same convention as
  // AssetCard's asset-summary query above.
  const assetsQ = useQuery({
    queryKey: ["assets-for-chat-link"],
    queryFn: () => apiClient.assets(),
    enabled: linkOpen,
    staleTime: 60_000,
  });
  const linkAsset = useMutation({
    mutationFn: (assetId: number) => apiClient.intakeFileToAsset(fileMeta!.fileId, { asset_id: assetId }),
    onSuccess: () => {
      setLinkOpen(false); setLinkQuery(""); setLinkErr(false); setLinkedOk(true);
      qc.invalidateQueries({ queryKey: ["conversation"] });
      window.setTimeout(() => setLinkedOk(false), 6000);
    },
    onError: () => setLinkErr(true),
  });
  const submitLinkAsset = () => {
    const q = linkQuery.trim().toLowerCase();
    const hit = (assetsQ.data ?? []).find((a) => a.tag.toLowerCase() === q);
    if (!hit) { setLinkErr(true); return; }
    linkAsset.mutate(hit.id);
  };

  return (
    // data-ev-id: the scroll-to-original target a quote block's click looks
    // up (see scrollToEvent above) — every real event bubble carries it.
    <div className={staff ? "self-start" : "self-end"} style={{ maxWidth: "82%" }} data-ev-id={e.id}>
      <div className="flex items-end gap-1">
        {(onAccept || onReply || onStamp || onDraft || (isStaff && fileMeta)) && (
          <div className="flex items-center gap-1 shrink-0" style={{ marginBottom: 2 }}>
            {onAccept && actionsOpen && (
              <button type="button" disabled={accepting}
                      onClick={() => { onAccept(); setActionsOpen(false); }}
                      className="font-mono inline-flex items-center gap-1 px-2 py-0.5 rounded-full disabled:opacity-40 hover:border-accent hover:text-accent-deep"
                      style={{ fontSize: 9.5, color: "var(--ink-3)", background: "var(--paper-2)",
                               border: "1px solid var(--hairline)", whiteSpace: "nowrap" }}>
                <ClipboardCheck size={10} /> {accepting ? "กำลังรับงาน…" : "รับเป็นงาน"}
              </button>
            )}
            {onAccept && (
              <button type="button" onClick={() => setActionsOpen((v) => !v)}
                      aria-label="จัดการข้อความ" aria-expanded={actionsOpen}
                      title="รับข้อความนี้เป็นงาน" className="p-0.5">
                <Settings size={12} style={{ color: actionsOpen ? "var(--accent-deep)" : "var(--ink-4)" }} />
              </button>
            )}
            {onStamp && stampConfirmOpen && (
              // Immutable action, no un-stamp — the confirm chip is the only
              // guard against a stray click, same idiom as onAccept's.
              <button type="button" disabled={stamping}
                      onClick={() => { onStamp(); setStampConfirmOpen(false); }}
                      className="font-mono inline-flex items-center gap-1 px-2 py-0.5 rounded-full disabled:opacity-40 hover:border-accent hover:text-accent-deep"
                      style={{ fontSize: 9.5, color: "var(--ink-3)", background: "var(--paper-2)",
                               border: "1px solid var(--hairline)", whiteSpace: "nowrap" }}>
                <Landmark size={10} /> {stamping ? "กำลังตรา…" : "ตราเป็นบันทึกการตัดสินใจ?"}
              </button>
            )}
            {onStamp && (
              <button type="button" onClick={() => setStampConfirmOpen((v) => !v)}
                      aria-label="ตราเป็นบันทึกการตัดสินใจ" aria-expanded={stampConfirmOpen}
                      title="ตราข้อความนี้เป็นบันทึกการตัดสินใจ" className="p-0.5">
                <Landmark size={13} style={{ color: stampConfirmOpen ? "var(--accent-deep)" : "var(--ink-4)" }} />
              </button>
            )}
            {onReply && (
              <button type="button" onClick={onReply} aria-label="ตอบกลับ" title="ตอบกลับ" className="p-0.5">
                <Reply size={13} style={{ color: "var(--ink-4)" }} />
              </button>
            )}
            {onDraft && (
              // Single click — no two-step confirm (see the prop docstring
              // above): it only drafts INTO the composer, it never sends.
              <button type="button" onClick={onDraft} disabled={drafting}
                      aria-label="MODS ร่างคำตอบ" title="MODS ร่างคำตอบ — ตรวจก่อนส่งเสมอ"
                      className="p-0.5 disabled:opacity-60">
                {drafting
                  ? <Loader2 size={13} className="animate-spin" style={{ color: "var(--accent)" }} />
                  : <Sparkles size={13} style={{ color: "var(--ink-4)" }} />}
              </button>
            )}
            {isStaff && fileMeta && (
              // Single-click toggle (no two-step confirm — see the docstring
              // above): opens the inline datalist row below the bubble.
              <button type="button" onClick={() => { setLinkOpen((v) => !v); setLinkErr(false); }}
                      aria-label="ผูกกับ asset" aria-expanded={linkOpen}
                      title="ผูกไฟล์นี้กับ asset" className="p-0.5">
                <LinkIcon size={13} style={{ color: linkOpen ? "var(--accent-deep)" : "var(--ink-4)" }} />
              </button>
            )}
          </div>
        )}
        <div className="font-mono px-2.5 py-1.5" style={{
          fontSize: 12, lineHeight: 1.45,
          background: isNote ? "rgba(180,83,9,0.1)" : staff ? "var(--paper)" : "rgba(var(--accent-rgb) / 0.1)",
          border: isNote ? "1px dashed var(--warn)" : staff ? "1px solid var(--hairline)" : "none",
          color: "var(--ink-1)",
          borderRadius: staff ? "10px 10px 10px 2px" : "10px 10px 2px 10px" }}>
          {isNote
            ? <div style={{ fontSize: 9.5, color: "var(--warn)", marginBottom: 2, fontWeight: 700 }}>โน้ตภายใน</div>
            : staff && <div style={{ fontSize: 9.5, color: "var(--accent-deep)", marginBottom: 2 }}>ทีม XZYNEXA</div>}
          {replyMeta && (
            <div onClick={() => scrollToEvent(replyMeta.id)} role="button" tabIndex={0}
                 onKeyDown={(ev) => { if (ev.key === "Enter") scrollToEvent(replyMeta.id); }}
                 className="cursor-pointer mb-1" data-testid="reply-quote"
                 style={{ background: "rgba(0,0,0,0.04)", borderLeft: "2px solid var(--accent)",
                          borderRadius: 6, padding: "4px 6px" }}>
              <div className="truncate" style={{ fontSize: 9.5, fontWeight: 600, color: "var(--ink-2)" }}>{replyMeta.actor}</div>
              <div className="flex items-center gap-1" style={{ fontSize: 10.5, color: "var(--ink-3)" }}>
                {replyMeta.kind === "file" && <FileText size={11} style={{ flexShrink: 0 }} />}
                <span className="truncate">{replyMeta.body}</span>
              </div>
            </div>
          )}
          {fileMeta
            ? (fileMeta.fileKind === "photo"
                ? <PhotoThumb file={{ id: fileMeta.fileId, filename: fileMeta.filename, kind: "photo", annotation: null }}
                              deliveryId={e.submission_id ?? 0} />
                : <FileChip file={{ id: fileMeta.fileId, filename: fileMeta.filename, kind: fileMeta.fileKind }} />)
            : (e.body ? renderBody(e.body) : null)}
          {/* Photo-to-asset chat linking — the "→ TAG" chip only ever
              appears once the backend's live lookup (_augment_file_meta)
              confirms the file's CURRENT routing; both viewers see it (the
              link itself isn't a staff secret, only the action that makes
              it is staff-gated above). */}
          {fileMeta?.assetTag && (
            <button type="button" data-testid="file-asset-chip"
                    onClick={() => fileMeta.assetId != null && navigate(`/assets/${fileMeta.assetId}`)}
                    className="font-mono flex items-center gap-1 mt-1.5 pt-1.5"
                    style={{ fontSize: 10, color: "var(--accent-deep)", borderTop: "1px solid var(--hairline)" }}>
              <Hexagon size={10} style={{ flexShrink: 0 }} />
              <span>→ {fileMeta.assetTag}</span>
            </button>
          )}
          {decision && (
            // Formal ink tones (not amber) — this is a record, not an alert.
            // Renders for BOTH viewers, unconditionally, once meta.decision
            // exists — see decisionMeta above.
            <div data-testid="decision-stamp" className="flex items-center gap-1 mt-1.5 pt-1.5"
                 style={{ borderTop: "1px solid var(--hairline)" }}>
              <Landmark size={11} style={{ color: "var(--ink-3)", flexShrink: 0 }} />
              <span className="font-mono" style={{ fontSize: 9.5, color: "var(--ink-3)" }}>
                บันทึกการตัดสินใจ — ตราโดย {decision.by} · {fmtWhen(decision.at)}
              </span>
            </div>
          )}
        </div>
      </div>
      {linkOpen && (
        // Same inline datalist idiom as ChatDock's ⬡ asset-focus picker
        // (openAssetPick/pickAsset) — a plain text input against a
        // datalist of tags, confirmed by Enter or the "ผูก" button.
        <div className="flex items-center gap-1.5 mt-1">
          <input autoFocus list={`chat-link-asset-opts-${e.id}`} value={linkQuery} aria-label="แท็ก asset"
                 onChange={(ev) => { setLinkQuery(ev.target.value); if (linkErr) setLinkErr(false); }}
                 onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); submitLinkAsset(); } if (ev.key === "Escape") setLinkOpen(false); }}
                 placeholder="เช่น V-201" className="min-w-0 px-2 py-1 outline-none font-mono"
                 style={{ width: 110, fontSize: 10.5, background: "var(--paper-2)",
                          border: "1px solid var(--hairline)", borderRadius: 10, color: "var(--ink-1)" }} />
          <datalist id={`chat-link-asset-opts-${e.id}`}>
            {(assetsQ.data ?? []).map((a) => <option key={a.id} value={a.tag} />)}
          </datalist>
          <button type="button" onClick={submitLinkAsset} disabled={linkAsset.isPending}
                  className="font-mono px-2 py-1 rounded-full shrink-0 disabled:opacity-40"
                  style={{ fontSize: 9.5, color: "var(--accent-deep)", border: "1px solid var(--accent)" }}>
            {linkAsset.isPending ? "กำลังผูก…" : "ผูก"}
          </button>
        </div>
      )}
      {linkErr && (
        <div className="font-mono mt-0.5" style={{ fontSize: 9.5, color: "var(--err)" }}>ไม่พบ asset นี้</div>
      )}
      {linkedOk && (
        <div className="font-mono mt-0.5" style={{ fontSize: 9.5, color: "var(--ok)" }}>ผูกกับ asset แล้ว</div>
      )}
      <div className={`font-mono ${staff ? "" : "text-right"}`} style={{ fontSize: 8.5, color: "var(--ink-4)", marginTop: 2 }}>
        {seenAt ? `เห็นแล้ว ${fmtTime(seenAt)}` : fmtWhen(e.created_at)}
      </div>
    </div>
  );
}
